import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { FitnessPlanService } from '../fitness-plan.service';
import { FitnessMemberBasicDetailsComponent } from '../fitness-member-basic-details/fitness-member-basic-details.component';
// import { FitnessPlanHelperService } from '../../services/fitness-plan-helper.service';
import { ExerciseSet } from '../../models/exercise-set.interface';

import { DateUtilityService } from '../../services/date-utility.service';
import { ExerciseValidationService } from '../../services/exercise-validation.service';
import { ExerciseStatisticsService } from '../../services/exercise-statistics.service';
import { ExerciseDataService } from '../../services/exercise-data.service';
import { TemplateManagementService } from '../../services/template-management.service';
import { FormInputService } from '../../services/form-input.service';
import { UiStateService } from '../../services/ui-state.service';
import { FitnessPlanPersistenceService } from '../../services/fitness-plan-persistence.service';
import { DataPopulationService } from '../../services/data-population.service';

interface Exercise {
  id: number;
  exercise: string;
  exerciseId?: string; // Add exerciseId for API operations
  allotmentId?: string; // Add allotmentId for updating existing allotments
  sets: ExerciseSet[];
  section?: 'warmup' | 'main' | 'cooldown'; // Add section property
  isMarkedForDeletion?: boolean; // Track if exercise is marked for deletion
  isRestDayPlaceholder?: boolean; // Internal flag for temporary rest-day UI row
}

interface DateItem {
  date: Date;
  formattedDate: string;
  dayName: string;
  isExpanded: boolean;
  exercises: Exercise[];
  isRestDay: boolean;
  restDayAllotmentId?: string; // Add property to store rest day allotment ID
  backupExercises?: Exercise[]; // Add property to store backup exercises for rest day restoration
}

@Component({
  selector: 'app-create-fitness-plan',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    FitnessMemberBasicDetailsComponent,
  ],
  templateUrl: './create-fitness-plan.component.html',
  styleUrl: './create-fitness-plan.component.css',
})
export class CreateFitnessPlanComponent {
  memberId: string = '';
  memberDetails: any = {};

  // Edit mode properties
  isEditMode: boolean = false;
  existingAllotments: any[] = [];

  // Delete confirmation properties
  showDeleteConfirmation: boolean = false;
  pendingDeleteAction: (() => void) | null = null;
  deleteConfirmationMessage: string = '';

  // Staged deletion properties
  pendingDeletions: Set<string> = new Set(); // Track allotment IDs marked for deletion
  showBulkDeleteConfirmation: boolean = false;
  bulkDeleteCount: number = 0;

  // Validation error properties
  showValidationErrors: boolean = false;
  validationErrors: string[] = [];

  // Empty dates warning properties
  showEmptyDatesWarning: boolean = false;
  emptyDatesCount: number = 0;
  pendingSubmitAction: (() => void) | null = null;

  // No dates to update warning properties
  showNoDatesToUpdateWarning: boolean = false;

  // Date range selection
  startDate: Date | null = null;
  endDate: Date | null = null;
  minDate: Date | null = null; // No restriction on past dates
  
  // Fitness plan name
  fitnessPlanName: string = '';
  hasExistingPlan: boolean = false;

  // Plan details
  planName: string = '';
  description: string = '';
  duration: number = 1; // weeks - default to 1 week to match auto-selected end date
  durationInDays: number = 7; // days - for precise display

  isSubmitting: boolean = false; // show loader overlay while waiting
  
  // Exercise allotments data for restore functionality
  exerciseAllotments: any[] = []; // Store fetched exercise allotments
  isTemplateSelected: boolean = false; // Track if template is selected

  // Exercise section configurations for shared layout
  exerciseSections = [
    {
      key: 'warmup' as 'warmup',
      title: '🔥 Warm Up Exercises',
      color: '#28a745',
      background: '#f8fffe',
      border: '#e8f5e8',
      buttonBg: '#28a745',
    },
    {
      key: 'main' as 'main',
      title: '💪 Main Exercises',
      color: '#05445e',
      background: '#f8f9ff',
      border: '#e8e8ff',
      buttonBg: '#05445e',
    },
    {
      key: 'cooldown' as 'cooldown',
      title: '🧘 Cool Down Exercises',
      color: '#17a2b8',
      background: '#f8fdff',
      border: '#e8f4f8',
      buttonBg: '#17a2b8',
    },
  ];

  // Exercise data for current step
  availableExercises: string[] = [];
  availableExercisesData: any[] = []; // Store full exercise objects with IDs

  // Current section being edited
  currentSection: 'warmup' | 'main' | 'cooldown' = 'warmup';

  // Template-related properties
  availableTemplates: any[] = [];
  selectedTemplateId: string | null = null;
  selectedTemplate: any = null;
  isLoadingTemplates: boolean = false;

  // Date list for Step 2 accordion
  dateList: DateItem[] = [];

  selectedDateIndex: number = 0;

  // Step management
  currentStep: number = 1;
  totalSteps: number = 3; // Step 1: Date Range, Step 2: Exercises, Step 3: Success

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fitnessPlanService: FitnessPlanService,
    // private helperService: FitnessPlanHelperService,
    private dateUtilityService: DateUtilityService,
    private exerciseValidationService: ExerciseValidationService,
    private exerciseStatisticsService: ExerciseStatisticsService,
    private exerciseDataService: ExerciseDataService,
    private templateManagementService: TemplateManagementService,
    private formInputService: FormInputService,
    private uiStateService: UiStateService,
    private fitnessPlanPersistenceService: FitnessPlanPersistenceService,
    private dataPopulationService: DataPopulationService
  ) {}

  ngOnInit(): void {
    this.memberId = this.route.snapshot.queryParamMap.get('memberId') || '';

    console.log('Component initialized - Member ID:', this.memberId);

    // Set next 7 days as default date range before loading other data
    this.setCurrentWeekAsDefault();

    this.loadMemberDetails();
    this.loadTemplates();

    // Check for existing fitness plan name when page loads
    this.checkForExistingFitnessPlan();

    // Load exercises first, then automatically check for existing allotments
    this.loadExercises().then(() => {
      console.log('Exercises loaded, now checking for existing allotments...');
      this.checkAndLoadExistingAllotments();
    });
  }

  // Method to set next 7 days as default date range
  setCurrentWeekAsDefault(): void {
    const today = new Date();

    // Start from today
    const startDate = new Date(today);
    startDate.setHours(0, 0, 0, 0); // Set to start of day

    // End date is 6 days from today (7 days total including today)
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + 6);
    endDate.setHours(23, 59, 59, 999); // Set to end of day

    // Set the date range
    this.startDate = startDate;
    this.endDate = endDate;

    console.log('Set next 7 days as default:', startDate, 'to', endDate);

    // Calculate duration and generate date list
    this.onEndDateChange();
  }

  // Method to check for existing fitness plan name
  checkForExistingFitnessPlan(startDate?: Date, endDate?: Date) {
    if (!this.memberId) return;

    const start = startDate || this.startDate || new Date();
    const end = endDate || this.endDate || new Date();


    this.fitnessPlanService
      .checkIsFitnessPlanOngoing(this.memberId, start.toISOString(), end.toISOString())
      .subscribe({
        next: (res: any) => {
          const name = res?.isExist ? res?.fitnessPlan?.fitnessPlanName : '';
          if (!name) {
            this.hasExistingPlan = false;
            this.fitnessPlanName = '';
            return;
          }
          
          // Fix timezone issue by extracting date parts directly from API response
          const formatAPIDateForComparison = (isoDate: string): string => {
            if (!isoDate) return '';
            try {
              // Extract just the date part to avoid timezone conversion issues
              const datePart = isoDate.split('T')[0]; // Extract "2025-11-02"
              const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
              
              // Create date in local timezone using the extracted components
              const localDate = new Date(year, month - 1, day);
              return this.dateUtilityService.formatDateForAPI(localDate);
            } catch (error) {
              console.error('Error formatting API date:', isoDate, error);
              return '';
            }
          };
          
          const planStart = formatAPIDateForComparison(res.fitnessPlan.startDate);
          const planEnd = formatAPIDateForComparison(res.fitnessPlan.endDate);
          const selStart = this.dateUtilityService.formatDateForAPI(start);
          const selEnd = this.dateUtilityService.formatDateForAPI(end);

          const within = selStart >= planStart && selStart <= planEnd && selEnd >= planStart && selEnd <= planEnd;

          this.hasExistingPlan = within;
          this.fitnessPlanName = within ? name : '';
        },
        error: () => {
          this.hasExistingPlan = false;
          this.fitnessPlanName = '';
        },
      });
  }

  loadMemberDetails() {
    // Try to get member data from router state first
    const { memberData } = window.history.state;

    if (memberData && memberData.id === this.memberId) {
      // Use data from router state (from previous screen)
      this.memberDetails = memberData;
      this.planName = `${this.memberDetails.firstName} Fitness Plan`;
      console.log('Using member data from router state - no API call needed');
    } else {
      // Fallback to API call if no router state data or memberId mismatch
      if (this.memberId) {
        this.fitnessPlanService.getMemberDetails(this.memberId).subscribe(
          (response: any) => {
            this.memberDetails = response.result;
            this.planName = `${this.memberDetails.firstName} Fitness Plan`;
            console.log('Loaded member data from API call');
          },
          (error) => {
            console.error('Error loading member details:', error);
          }
        );
      }
    }
  }

  loadExercises(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.fitnessPlanService.getExercises().subscribe(
        (response: any) => {
          if (
            response &&
            response.exercises &&
            Array.isArray(response.exercises)
          ) {
            // Store full exercise objects for ID mapping
            this.availableExercisesData = response.exercises;
            // Extract the 'name' property from each exercise object for dropdown display
            this.availableExercises = response.exercises.map(
              (exercise: any) => exercise.name || exercise
            );
            console.log('Loaded exercises from API:', this.availableExercises);
            console.log('Full exercise data:', this.availableExercisesData);
            resolve();
          } else {
            console.warn(
              'Invalid exercises response format, using empty array'
            );
            this.availableExercises = [];
            this.availableExercisesData = [];
            resolve();
          }
        },
        (error) => {
          console.error('Error loading exercises:', error);
          // Set empty array when error occurs (no fallback exercises)
          this.availableExercises = [];
          this.availableExercisesData = [];
          resolve(); // Still resolve to continue execution
        }
      );
    });
  }

  // Method to automatically check and load existing allotments
  checkAndLoadExistingAllotments() {
    if (!this.memberId) return;

    console.log('Checking for existing allotments for member:', this.memberId);
    this.fitnessPlanService.getExerciseAllotments(this.memberId).subscribe(
      (response: any) => {
        console.log('Raw API response for existing allotments:', response);

        // Try different possible response formats
        let allotments: any[] = [];

        if (
          response &&
          response.exerciseAllotments &&
          Array.isArray(response.exerciseAllotments)
        ) {
          // Actual format: { exerciseAllotments: [...] }
          allotments = response.exerciseAllotments;
        } else if (
          response &&
          response.allotments &&
          Array.isArray(response.allotments)
        ) {
          // Alternative format: { allotments: [...] }
          allotments = response.allotments;
        } else if (response && Array.isArray(response)) {
          // Direct array format: [...]
          allotments = response;
        } else if (
          response &&
          response.result &&
          Array.isArray(response.result)
        ) {
          // Result wrapper format: { result: [...] }
          allotments = response.result;
        } else {
          console.log('No existing allotments found or unrecognized format');
          return;
        }

        if (allotments.length > 0) {
          this.existingAllotments = allotments;
          this.isEditMode = true; // Set to edit mode since we found existing data
          console.log(
            'Existing allotments found:',
            this.existingAllotments.length,
            'allotments - entering edit mode'
          );
          console.log('First allotment sample:', this.existingAllotments[0]);
          this.populateExistingData();
        } else {
          console.log('No allotments found - staying in create mode');
          this.isEditMode = false;
        }
      },
      (error) => {
        console.error('Error loading existing allotments:', error);
        this.isEditMode = false; // Stay in create mode if there's an error
      }
    );
  }

  generateDateList() {
    this.dateList = this.exerciseDataService.generateDateList(
      this.startDate!,
      this.endDate!,
      this.dateUtilityService
    );

    // Auto-apply template if one is selected
    if (this.selectedTemplate) {
      console.log('Auto-applying selected template to generated date list');
      // Add a small delay to ensure the dateList is fully populated
      setTimeout(() => {
        this.applyTemplateToDateRange();
      }, 100);
    }
  }

  toggleAccordion(index: number) {
    this.dateList[index].isExpanded = !this.dateList[index].isExpanded;
  }

  toggleDateRestDay(index: number) {
    const dateItem = this.dateList[index];
    const wasRestDay = dateItem.isRestDay;
    const willBeRestDay = !dateItem.isRestDay;

    // Check if we're in edit mode and handle existing data appropriately
    if (this.isEditMode && this.hasExistingAllotmentsForDate(dateItem.date)) {
      if (willBeRestDay) {
        // Converting workout day to rest day - stage existing exercises for deletion
        this.executeConvertToRestDay(index);
      } else {
        // Converting rest day to workout day - stage rest day for deletion
        this.executeConvertToWorkoutDay(index);
      }
      return;
    }

    // No existing data, proceed directly
    this.executeToggleRestDay(index);
  }

  private executeToggleRestDay(index: number) {
    const dateItem = this.dateList[index];
    dateItem.isRestDay = !dateItem.isRestDay;

    if (dateItem.isRestDay) {
      // When converting to rest day, preserve ALL exercises (both regular and marked for deletion)
      // but add a default empty exercise for the UI
      const regularExercises = dateItem.exercises.filter(
        (ex) => !ex.isMarkedForDeletion && !ex.isRestDayPlaceholder
      );
      const markedExercises = dateItem.exercises.filter(
        (ex) => ex.isMarkedForDeletion
      );
      
      // Store the regular exercises in a backup property for restoration
      dateItem.backupExercises = [...regularExercises];
      
      const defaultExercise = {
        id: 1,
        exercise: '',
        sets: [
          { setNumber: 1, reps: '', time: '', weights: '', status: 'new' },
        ],
        isRestDayPlaceholder: true,
      };

      // Keep marked exercises (hidden in UI) plus one empty exercise for rest day display
      dateItem.exercises = [...markedExercises, defaultExercise];

      console.log(
        'Preserved regular exercises for rest day:',
        regularExercises.length,
        'Marked exercises:',
        markedExercises.length
      );
      // Note: restDayAllotmentId will be set when the rest day is saved to the database
    } else {
      // When switching from rest day to workout day, restore the original exercises
      dateItem.restDayAllotmentId = undefined; // Clear the rest day allotment ID

      // Check if we have backup exercises to restore
      const backupExercises = dateItem.backupExercises || [];
      
      if (backupExercises.length > 0) {
        // Restore the original exercises
        const markedExercises = dateItem.exercises.filter(
          (ex) => ex.isMarkedForDeletion
        );
        dateItem.exercises = [...backupExercises, ...markedExercises];
        
        // Clear the backup
        delete dateItem.backupExercises;
        
        console.log(
          'Restored original exercises from backup:',
          backupExercises.length
        );
      } else {
        // No backup exercises, remove marked exercises and add default structure
        const unmarkedExercises = dateItem.exercises.filter(
          (ex: any) => !ex.isMarkedForDeletion && !ex.isRestDayPlaceholder
        );
        
        if (unmarkedExercises.length === 0) {
          // If no exercises left, add a default one
          dateItem.exercises = [
            {
              id: 1,
              exercise: '',
              sets: [
                { setNumber: 1, reps: '', time: '', weights: '', status: 'new' },
              ],
              section: 'warmup' as const,
            },
            {
              id: 2,
              exercise: '',
              sets: [
                { setNumber: 1, reps: '', time: '', weights: '', status: 'new' },
              ],
              section: 'main' as const,
            },
            {
              id: 3,
              exercise: '',
              sets: [
                { setNumber: 1, reps: '', time: '', weights: '', status: 'new' },
              ],
              section: 'cooldown' as const,
            },
          ];
        } else {
          dateItem.exercises = unmarkedExercises;
        }
      }
    }
  }

  private executeConvertToRestDay(index: number) {
    const dateItem = this.dateList[index];

    console.log(
      `Converting ${dateItem.formattedDate} to rest day - staging existing exercises for deletion`
    );
    console.log(
      'Exercises before marking:',
      dateItem.exercises.map((ex) => ({
        name: ex.exercise,
        allotmentId: ex.allotmentId,
        isMarkedForDeletion: ex.isMarkedForDeletion,
      }))
    );

    // Mark all existing exercises for deletion instead of making immediate API calls
    dateItem.exercises.forEach((exercise) => {
      if (exercise.allotmentId) {
        // Only mark exercises with allotment IDs (saved to database)
        exercise.isMarkedForDeletion = true;
        console.log(
          `Marked exercise '${exercise.exercise}' for deletion (allotmentId: ${exercise.allotmentId})`
        );
      } else {
        console.log(
          `Skipping exercise '${exercise.exercise}' - no allotmentId (not saved to database)`
        );
      }
    });

    console.log(
      'Exercises after marking:',
      dateItem.exercises.map((ex) => ({
        name: ex.exercise,
        allotmentId: ex.allotmentId,
        isMarkedForDeletion: ex.isMarkedForDeletion,
      }))
    );

    // Proceed with the conversion
    this.executeToggleRestDay(index);
    this.showDeleteConfirmation = false;
    this.pendingDeleteAction = null;

    console.log(
      `Successfully staged ${
        dateItem.exercises.filter((ex) => ex.isMarkedForDeletion).length
      } exercises for deletion`
    );
  }

  private executeConvertToWorkoutDay(index: number) {
    const dateItem = this.dateList[index];

    console.log(
      `Converting ${dateItem.formattedDate} to workout day - staging rest day entry for deletion`
    );

    // If there's an existing rest day allotment, stage it for deletion
    if (dateItem.restDayAllotmentId) {
      this.pendingDeletions.add(dateItem.restDayAllotmentId);
      console.log(
        `Staged rest day allotment ${dateItem.restDayAllotmentId} for deletion`
      );
    } else {
      // Try to find the rest day allotment in existing allotments
      const dateString = this.dateUtilityService.formatDateForAPI(
        dateItem.date
      );
      const restDayAllotment = this.existingAllotments.find(
        (allotment) =>
          this.dateUtilityService.formatISODateForComparison(allotment.date) ===
            dateString &&
          (allotment.phase === 'restday' || allotment.exerciseId === '')
      );

      if (restDayAllotment?.id) {
        this.pendingDeletions.add(restDayAllotment.id);
        console.log(
          `Found and staged rest day allotment ${restDayAllotment.id} for deletion`
        );
      } else {
        console.log(
          `No rest day allotment found for ${dateString} - may be a new rest day`
        );
      }
    }

    // Clear the stored rest day allotment ID as it will be deleted
    dateItem.restDayAllotmentId = undefined;

    // Proceed with the conversion
    this.executeToggleRestDay(index);
    this.showDeleteConfirmation = false;
    this.pendingDeleteAction = null;

    console.log(
      `Successfully converted ${dateItem.formattedDate} to workout day`
    );
  }

  // Helper method to check if a date has existing allotments in the database
  private hasExistingAllotmentsForDate(date: Date): boolean {
    const dateString = this.dateUtilityService.formatDateForAPI(date);
    return this.existingAllotments.some(
      (allotment) =>
        this.dateUtilityService.formatISODateForComparison(allotment.date) ===
        dateString
    );
  }

  // Method to confirm and execute pending delete action
  confirmDelete() {
    if (this.pendingDeleteAction) {
      this.pendingDeleteAction();
    }
  }

  // Method to cancel delete confirmation
  cancelDelete() {
    this.showDeleteConfirmation = false;
    this.pendingDeleteAction = null;
    this.deleteConfirmationMessage = '';
  }

  // Method to check for empty dates (dates with no exercises)
  private checkForEmptyDates(): number {
    let emptyDatesCount = 0;

    this.dateList.forEach((dateItem) => {
      // Skip rest days as they are intentionally empty
      if (dateItem.isRestDay) {
        return;
      }

      // Skip past dates (they won't be sent to API) using the same logic as UI
      if (this.isDateNonEditable(dateItem.date)) {
        return; // Skip past dates - they're not being updated
      }

      // Check if date has no exercises or all exercises are empty
      const hasValidExercises = dateItem.exercises.some((exercise) => {
        return exercise.exercise && exercise.exercise.trim() !== '';
      });

      if (!hasValidExercises) {
        emptyDatesCount++;
      }
    });

    return emptyDatesCount;
  }

  // Method to confirm and continue with empty dates submission
  confirmEmptyDatesSubmission() {
    this.showEmptyDatesWarning = false;
    if (this.pendingSubmitAction) {
      this.pendingSubmitAction();
      this.pendingSubmitAction = null;
    }
  }

  // Method to cancel empty dates warning
  cancelEmptyDatesSubmission() {
    this.showEmptyDatesWarning = false;
    this.pendingSubmitAction = null;
    this.emptyDatesCount = 0;
  }

  addExerciseToDate(
    dateIndex: number,
    section: 'warmup' | 'main' | 'cooldown' = 'main'
  ) {
    const exercises = this.dateList[dateIndex].exercises;
    this.exerciseDataService.addNewExerciseToArray(exercises, section);
  }

  removeExerciseFromDate(dateIndex: number, exerciseId: number) {
    const exercises = this.dateList[dateIndex].exercises;
    const exercise = exercises.find((e) => e.id === exerciseId);

    if (!exercise) return;

    // If in edit mode and exercise has an allotmentId (exists in DB), mark for staged deletion
    if (this.isEditMode && exercise.allotmentId) {
      if (exercise.isMarkedForDeletion) {
        // If already marked for deletion, unmark it (undo delete)
        exercise.isMarkedForDeletion = false;
        this.pendingDeletions.delete(exercise.allotmentId);
        console.log('Unmarked exercise for deletion:', exercise.allotmentId);
      } else {
        // Mark for deletion
        exercise.isMarkedForDeletion = true;
        this.pendingDeletions.add(exercise.allotmentId);
        console.log('Marked exercise for deletion:', exercise.allotmentId);
      }
      return;
    }

    // No DB record or not in edit mode, remove directly and add new empty exercise
    this.removeExerciseAndAddEmptyIfNeeded(dateIndex, exerciseId);
  }

  // Helper method to check if an exercise exists in the database
  hasExistingExercise(exercise: Exercise): boolean {
    return !!(exercise.allotmentId && this.isEditMode);
  }

  // Helper method to remove exercise and add empty one if it's the last in section
  private removeExerciseAndAddEmptyIfNeeded(
    dateIndex: number,
    exerciseId: number
  ) {
    const exercises = this.dateList[dateIndex].exercises;
    const exercise = exercises.find((e) => e.id === exerciseId);

    if (!exercise) return;

    const section = exercise.section;
    const sectionExercises = this.getActiveExercisesBySection(
      dateIndex,
      section!
    );

    // Remove the exercise
    this.dateList[dateIndex].exercises = exercises.filter(
      (e) => e.id !== exerciseId
    );

    // If this was the last exercise in the section, add a new empty one
    if (sectionExercises.length === 1) {
      this.addExerciseToDate(dateIndex, section!);
    }
  }

  /**
   * Check if restore button should be shown for a specific exercise
   */
  shouldShowRestoreButton(exercise: Exercise, dateIndex: number): boolean {
    // Only show if template is selected and exercise has an exerciseId
    if (!this.isTemplateSelected || !exercise.exerciseId) {
      return false;
    }

    const dateItem = this.dateList[dateIndex];
    const dateString = this.formatDateForApi(dateItem.date);

    // Check if there's existing allotment data for this exercise, date, and member
    const existingAllotment = this.exerciseAllotments.find(
      (allotment) =>
        allotment.exerciseId === exercise.exerciseId &&
        allotment.memberId === this.memberId &&
        allotment.date === dateString &&
        allotment.phase === exercise.section
    );

    if (!existingAllotment) {
      return false;
    }

    // Only show restore if current exercise data differs from the allotment data
    return !this.isExerciseEqualToAllotment(exercise, existingAllotment);
  }

  /**
   * Restore exercise data from existing allotment
   */
  restoreExerciseData(exercise: Exercise, dateIndex: number): void {
    if (!this.isTemplateSelected || !exercise.exerciseId) {
      return;
    }

    const dateItem = this.dateList[dateIndex];
    const dateString = this.formatDateForApi(dateItem.date);

    // Find the existing allotment data
    const existingAllotment = this.exerciseAllotments.find(
      (allotment) =>
        allotment.exerciseId === exercise.exerciseId &&
        allotment.memberId === this.memberId &&
        allotment.date === dateString &&
        allotment.phase === exercise.section
    );

    if (existingAllotment && existingAllotment.sets && existingAllotment.sets.length > 0) {
      // Restore the sets data
      exercise.sets = existingAllotment.sets.map((set: any, index: number) => {
        // Convert time from seconds to MM:SS format
        const timeInSeconds = set.time || 0;
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = timeInSeconds % 60;
        const timeString = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

        return {
          setNumber: index + 1,
          reps: set.reps || 0,
          weights: set.weights || 0,
          time: timeString,
          timeMinutes: minutes,
          timeSeconds: seconds,
          status: set.status || 'new'
        };
      });

      // Set the allotment ID for tracking
      exercise.allotmentId = existingAllotment.id;

      console.log('Restored exercise data:', exercise);
    }
  }

  /**
   * Normalize values for comparison - treat empty string, null, undefined, and 0 as equivalent
   */
  private normalizeValueForComparison(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    
    const stringValue = value.toString().trim();
    
    // If the string value is empty or represents 0, normalize to empty string
    if (stringValue === '' || stringValue === '0') {
      return '';
    }
    
    return stringValue;
  }

  /**
   * Compare current exercise data with an allotment to determine equality
   */
  private isExerciseEqualToAllotment(exercise: Exercise, allotment: any): boolean {
    // Basic checks
    if (exercise.exerciseId !== allotment.exerciseId) return false;
    if (exercise.section !== allotment.phase) return false;

    const currentSets = Array.isArray(exercise.sets) ? exercise.sets : [];
    const allotmentSets = Array.isArray(allotment.sets) ? allotment.sets : [];

    // Different number of sets means different
    if (currentSets.length !== allotmentSets.length) return false;

    // Normalize and compare each set
    for (let i = 0; i < currentSets.length; i++) {
      const cur = currentSets[i] as any;
      const all = allotmentSets[i] as any;

      // Normalize reps comparison - treat empty string and 0 as equivalent
      const curReps = this.normalizeValueForComparison(cur.reps);
      const allReps = this.normalizeValueForComparison(all.reps);
      if (curReps !== allReps) return false;

      // Normalize weights comparison - treat empty string and 0 as equivalent
      const curWeights = this.normalizeValueForComparison(cur.weights);
      const allWeights = this.normalizeValueForComparison(all.weights);
      if (curWeights !== allWeights) return false;

      // Allotment time is in seconds; current may be in timeMinutes/timeSeconds or string MM:SS
      const allTimeSeconds = Number(all.time || 0);
      const curMinutes = Number(cur.timeMinutes ?? (typeof cur.time === 'string' && cur.time.includes(':') ? cur.time.split(':')[0] : 0)) || 0;
      const curSeconds = Number(cur.timeSeconds ?? (typeof cur.time === 'string' && cur.time.includes(':') ? cur.time.split(':')[1] : 0)) || 0;
      const curTimeSeconds = curMinutes * 60 + curSeconds;
      if (curTimeSeconds !== allTimeSeconds) return false;
    }

    return true;
  }

  /**
   * Format date for API comparison (YYYY-MM-DDTHH:mm:ss.sssZ format)
   */
  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00Z`;
  }

  /**
   * Auto-fill exercises from allotments when no template is selected
   */
  autoFillExercisesFromAllotments(): void {
    if (!this.memberId || !this.startDate || !this.endDate || this.isTemplateSelected) {
      return;
    }

    console.log('Auto-filling exercises from allotments (no template selected)');
    
    // Fetch exercise allotments first
    this.fitnessPlanService.getExerciseAllotments(this.memberId).subscribe({
      next: (response: any) => {
        console.log('Allotments response for auto-fill:', response);
        
        let allotments: any[] = [];
        
        if (response && response.exerciseAllotments && Array.isArray(response.exerciseAllotments)) {
          allotments = response.exerciseAllotments;
        } else if (response && response.allotments && Array.isArray(response.allotments)) {
          allotments = response.allotments;
        } else if (Array.isArray(response)) {
          allotments = response;
        }

        // Filter allotments by date range
        const startDateString = this.formatDateForApi(this.startDate!);
        const endDateString = this.formatDateForApi(this.endDate!);
        
        const filteredAllotments = allotments.filter((allotment) => {
          const allotmentDate = allotment.date;
          return allotmentDate >= startDateString && allotmentDate <= endDateString;
        });

        console.log('Filtered allotments for auto-fill:', filteredAllotments);

        // Auto-fill exercises for each date
        this.dateList.forEach((dateItem, dateIndex) => {
          const dateString = this.formatDateForApi(dateItem.date);
          
          // Get allotments for this specific date
          const dateAllotments = filteredAllotments.filter(
            (allotment) => allotment.date === dateString
          );

          if (dateAllotments.length > 0) {
            this.autoFillDateFromAllotments(dateItem, dateIndex, dateAllotments);
          }
        });

        console.log('Auto-fill completed from allotments');
      },
      error: (error) => {
        console.error('Error fetching allotments for auto-fill:', error);
      }
    });
  }

  /**
   * Auto-fill exercises for a specific date from allotments
   */
  private autoFillDateFromAllotments(dateItem: DateItem, dateIndex: number, allotments: any[]): void {
    // Detect if this date is a rest day from allotments
    const restDayAllotment = allotments.find(a => a.phase === 'restday' || !a.exerciseId || a.exerciseId === '');

    if (restDayAllotment) {
      // Mark as rest day and set placeholder
      dateItem.isRestDay = true;
      dateItem.restDayAllotmentId = restDayAllotment.id;

      const defaultExercise = {
        id: 1,
        exercise: '',
        sets: [
          { setNumber: 1, reps: '', time: '', weights: '', status: 'new' },
        ],
        isRestDayPlaceholder: true,
      } as any;

      dateItem.exercises = [defaultExercise];
      return;
    }

    // Not a rest day: ensure flag and clear any placeholders before filling
    dateItem.isRestDay = false;
    dateItem.restDayAllotmentId = undefined;
    dateItem.exercises = [];

    // Group allotments by phase
    const phaseGroups = {
      warmup: allotments.filter(a => a.phase === 'warmup'),
      main: allotments.filter(a => a.phase === 'main'),
      cooldown: allotments.filter(a => a.phase === 'cooldown')
    };

    let exerciseId = Math.max(0, ...dateItem.exercises.map(ex => ex.id)) + 1;

    // Process each phase
    Object.keys(phaseGroups).forEach((phase: string) => {
      const phaseAllotments = phaseGroups[phase as keyof typeof phaseGroups];
      
      phaseAllotments.forEach((allotment) => {
        // Find the exercise name from availableExercisesData
        const exerciseData = this.availableExercisesData.find(
          (ex: any) => ex.id === allotment.exerciseId
        );
        
        if (exerciseData) {
          const exercise = this.createExerciseFromAllotment(
            allotment,
            exerciseData.name || exerciseData.exercise || 'Unknown Exercise',
            phase as 'warmup' | 'main' | 'cooldown',
            exerciseId++
          );
          
          dateItem.exercises.push(exercise);
        }
      });
    });

    console.log(`Auto-filled ${dateItem.exercises.length} exercises for date ${dateItem.formattedDate}`);
  }

  /**
   * Create an exercise object from allotment data
   */
  private createExerciseFromAllotment(
    allotment: any,
    exerciseName: string,
    section: 'warmup' | 'main' | 'cooldown',
    exerciseId: number
  ): any {
    let sets = [];

    if (allotment.sets && Array.isArray(allotment.sets)) {
      sets = allotment.sets.map((set: any, setIndex: number) => {
        // Convert time from seconds to MM:SS format
        const timeInSeconds = set.time || 0;
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = timeInSeconds % 60;
        const timeString = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

        return {
          setNumber: setIndex + 1,
          reps: set.reps || 0,
          time: timeString,
          timeMinutes: minutes,
          timeSeconds: seconds,
          weights: set.weights || 0,
          status: set.status || 'new',
        };
      });
    } else {
      // Create a default set if none provided
      sets = [
        {
          setNumber: 1,
          reps: '',
          time: '00:00',
          timeMinutes: 0,
          timeSeconds: 0,
          weights: '',
          status: 'new',
        },
      ];
    }

    return {
      id: exerciseId,
      exercise: exerciseName,
      exerciseId: allotment.exerciseId,
      allotmentId: allotment.id,
      sets: sets,
      section: section,
      isMarkedForDeletion: false,
    };
  }

  /**
   * Fetch exercise allotments for the selected date range
   */
  fetchExerciseAllotments(): void {
    if (!this.memberId || !this.startDate || !this.endDate) {
      return;
    }

    console.log('Fetching exercise allotments for member:', this.memberId);
    this.fitnessPlanService.getExerciseAllotments(this.memberId).subscribe({
      next: (response: any) => {
        console.log('Exercise allotments response:', response);
        
        let allotments: any[] = [];
        
        if (response && response.exerciseAllotments && Array.isArray(response.exerciseAllotments)) {
          allotments = response.exerciseAllotments;
        } else if (response && response.allotments && Array.isArray(response.allotments)) {
          allotments = response.allotments;
        } else if (Array.isArray(response)) {
          allotments = response;
        }

        // Filter allotments by date range
        const startDateString = this.formatDateForApi(this.startDate!);
        const endDateString = this.formatDateForApi(this.endDate!);
        
        this.exerciseAllotments = allotments.filter((allotment) => {
          const allotmentDate = allotment.date;
          return allotmentDate >= startDateString && allotmentDate <= endDateString;
        });

        console.log('Filtered exercise allotments:', this.exerciseAllotments.length, 'items');
        
        // If template has already been applied to dateList, add missing allotment exercises now
        // This handles the case where allotments load after the template was applied
        if (this.isTemplateSelected && this.dateList.length > 0 && this.dateList.some(d => d.exercises && d.exercises.length > 0)) {
          console.log('Template already applied, adding missing allotment exercises now');
          this.addAllotmentExercisesNotInTemplate();
        }
      },
      error: (error) => {
        console.error('Error fetching exercise allotments:', error);
        this.exerciseAllotments = [];
      }
    });
  }

  /**
   * Applies exercises from a specific date to all upcoming same weekdays
   */
  applyToUpcomingWeekdays(dateIndex: number) {
    if (this.dateList.length <= 1) return;

    const sourceDateItem = this.dateList[dateIndex];
    const weekdayName = this.getWeekdayName(sourceDateItem.date);

    // Check if there are upcoming same weekdays
    const upcomingSameWeekdays = this.getUpcomingSameWeekdays(dateIndex);

    if (upcomingSameWeekdays.length > 0) {
      this.dataPopulationService.applyExercisesToUpcomingWeekdays(
        sourceDateItem,
        this.dateList
      );
      console.log(
        `Applied exercises from ${weekdayName} to ${upcomingSameWeekdays.length} upcoming ${weekdayName}s`
      );

      // Button will automatically hide due to the areExercisesDifferentFromUpcoming check
    }
  }

  /**
   * Checks if there are upcoming same weekdays for the apply button
   */
  hasUpcomingSameWeekdays(dateIndex: number): boolean {
    // First check if there are upcoming same weekdays
    const upcomingDays = this.getUpcomingSameWeekdays(dateIndex);
    if (upcomingDays.length === 0) {
      return false;
    }

    // Then check if the current day has meaningful exercises AND they're different from upcoming days
    const currentDay = this.dateList[dateIndex];
    const hasChanges = this.hasExerciseChanges(currentDay);
    const isDifferentFromUpcoming =
      this.areExercisesDifferentFromUpcoming(dateIndex);

    console.log(`hasUpcomingSameWeekdays for index ${dateIndex}:`, {
      upcomingCount: upcomingDays.length,
      hasChanges,
      isDifferentFromUpcoming,
      shouldShow: hasChanges && isDifferentFromUpcoming,
    });

    return hasChanges && isDifferentFromUpcoming;
  }

  /**
   * Checks if a date item has meaningful exercise changes worth copying
   */
  private hasExerciseChanges(dateItem: any): boolean {
    if (!dateItem || !dateItem.exercises || dateItem.isRestDay) {
      return false;
    }

    // Check if any exercise has been selected or has meaningful data
    return dateItem.exercises.some((exercise: any) => {
      // Has an exercise selected
      if (exercise.exercise && exercise.exercise.trim() !== '') {
        return true;
      }

      // Has meaningful set data (reps, weights, or time)
      return (
        exercise.sets &&
        exercise.sets.some((set: any) => {
          return (
            (set.reps &&
              set.reps.toString().trim() !== '' &&
              set.reps !== '0') ||
            (set.weights &&
              set.weights.toString().trim() !== '' &&
              set.weights !== '0') ||
            (set.timeMinutes && set.timeMinutes > 0) ||
            (set.timeSeconds && set.timeSeconds > 0) ||
            (set.time && set.time.trim() !== '' && set.time !== '00:00')
          );
        })
      );
    });
  }

  /**
   * Checks if current day's exercises are different from upcoming same weekdays
   */
  private areExercisesDifferentFromUpcoming(dateIndex: number): boolean {
    const upcomingDays = this.getUpcomingSameWeekdays(dateIndex);
    if (upcomingDays.length === 0) {
      return false;
    }

    const currentDay = this.dateList[dateIndex];

    // Compare current day's exercises with the first upcoming same weekday
    // If they're different, we should show the apply button
    const firstUpcomingDay = upcomingDays[0];

    return !this.areExercisesEqual(
      currentDay.exercises,
      firstUpcomingDay.exercises
    );
  }

  /**
   * Deep comparison of two exercise arrays to check if they're equal
   */
  private areExercisesEqual(
    exercises1: Exercise[],
    exercises2: Exercise[]
  ): boolean {
    if (exercises1.length !== exercises2.length) {
      return false;
    }

    for (let i = 0; i < exercises1.length; i++) {
      const ex1 = exercises1[i];
      const ex2 = exercises2[i];

      // Compare exercise names
      if ((ex1.exercise || '').trim() !== (ex2.exercise || '').trim()) {
        return false;
      }

      // Compare exercise IDs
      if (ex1.exerciseId !== ex2.exerciseId) {
        return false;
      }

      // Compare sections
      if (ex1.section !== ex2.section) {
        return false;
      }

      // Compare sets
      if (!this.areSetsEqual(ex1.sets, ex2.sets)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Deep comparison of two sets arrays
   */
  private areSetsEqual(sets1: ExerciseSet[], sets2: ExerciseSet[]): boolean {
    if (sets1.length !== sets2.length) {
      return false;
    }

    for (let i = 0; i < sets1.length; i++) {
      const set1 = sets1[i];
      const set2 = sets2[i];

      if (
        (set1.reps || '').toString().trim() !==
          (set2.reps || '').toString().trim() ||
        (set1.weights || '').toString().trim() !==
          (set2.weights || '').toString().trim() ||
        (set1.time || '').trim() !== (set2.time || '').trim() ||
        (set1.timeMinutes || 0) !== (set2.timeMinutes || 0) ||
        (set1.timeSeconds || 0) !== (set2.timeSeconds || 0)
      ) {
        return false;
      }
    }

    return true;
  }

  /**
   * Gets the weekday name for display
   */
  getWeekdayName(date: Date): string {
    const weekdays = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    return weekdays[date.getDay()];
  }

  /**
   * Gets upcoming same weekdays for a given date index
   */
  private getUpcomingSameWeekdays(sourceDateIndex: number): DateItem[] {
    const sourceDateItem = this.dateList[sourceDateIndex];
    const sourceWeekday = sourceDateItem.date.getDay();

    return this.dateList.filter(
      (item, index) =>
        index > sourceDateIndex &&
        item.date.getDay() === sourceWeekday &&
        !item.isRestDay
    );
  }

  // Step navigation
  nextStep() {
    if (this.currentStep < this.totalSteps) {
      if (this.currentStep === 1 && this.isDateRangeValid()) {
        // Before moving to step 2, ensure template is applied if selected
        if (this.selectedTemplate && this.dateList.length > 0) {
          console.log('Applying template before moving to step 2');
          this.applyTemplateToDateRange();
        } else if (!this.isTemplateSelected && this.memberId && this.startDate && this.endDate) {
          // Auto-fill exercises from allotments when no template is selected
          console.log('Auto-filling exercises from allotments before moving to step 2');
          this.autoFillExercisesFromAllotments();
        }
        this.currentStep++;
      } else if (this.currentStep === 2) {
        this.currentStep++;
      }
    }
  }

  previousStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  isDateRangeValid(): boolean {
    return !!(this.startDate && this.endDate && this.startDate <= this.endDate);
  }

  isFitnessPlanNameValid(): boolean {
    return !!(this.fitnessPlanName && this.fitnessPlanName.trim() !== '');
  }

  isCurrentStepValid(): boolean {
    switch (this.currentStep) {
      case 1:
        return this.isDateRangeValid();
      case 2:
        // Step 2 is valid if we have at least one date in the list, fitness plan name is provided,
        // and each date can either be a rest day or have at least one properly configured exercise
        return (
          this.dateList.length > 0 &&
          this.isFitnessPlanNameValid() &&
          this.dateList.every((dateItem) => {
            if (dateItem.isRestDay) {
              return true; // Rest days are always valid
            }
            // Check if at least one exercise has required fields filled
            return dateItem.exercises.some(
              (exercise) =>
                exercise.exercise.trim() &&
                exercise.sets.length > 0 &&
                exercise.sets.some((set) => {
                  const repsValue =
                    typeof set.reps === 'string'
                      ? typeof set.reps === 'string'
                        ? set.reps.trim()
                        : String(set.reps).trim()
                      : set.reps !== null && set.reps !== undefined
                      ? String(set.reps).trim()
                      : '';
                  return repsValue && repsValue !== '0' && repsValue !== '';
                })
            );
          })
        );
      case 3:
        return true; // Success step is always valid
      default:
        return false;
    }
  }

  // Exercise management methods for date-based workflow
  addSetToExercise(dateIndex: number, exerciseId: number) {
    const exercise = this.dateList[dateIndex].exercises.find(
      (e) => e.id === exerciseId
    );
    if (exercise) {
      this.exerciseDataService.addSetToExercise(exercise);
    }
  }

  removeSetFromExercise(
    dateIndex: number,
    exerciseId: number,
    setIndex: number
  ) {
    const exercise = this.dateList[dateIndex].exercises.find(
      (e) => e.id === exerciseId
    );
    if (exercise && exercise.sets.length > 1) {
      exercise.sets.splice(setIndex, 1);
      // Renumber remaining sets
      exercise.sets.forEach((set, index) => {
        set.setNumber = index + 1;
      });
    }
  }

  createPlan() {
    // Check if all dates are in the past before proceeding
    if (this.checkForNoDatesToUpdate()) {
      this.showNoDatesToUpdateWarning = true;
      return; // Don't proceed with submission
    }

    // Validate fitness plan name before submission
    if (!this.isFitnessPlanNameValid()) {
      this.validationErrors = ['Fitness plan name is required'];
      this.showValidationErrors = true;
      return; // Don't proceed with submission
    }

    // Validate all exercises before submission
    const validationErrors = this.validateAllExercises();

    if (validationErrors.length > 0) {
      this.validationErrors = validationErrors;
      this.showValidationErrors = true;
      return; // Don't proceed with submission
    }

    // Check for empty dates before submission
    const emptyDatesCount = this.checkForEmptyDates();

    if (emptyDatesCount > 0) {
      this.emptyDatesCount = emptyDatesCount;
      this.showEmptyDatesWarning = true;
      this.pendingSubmitAction = () => this.checkBulkDeletionAndProceed();
      return; // Show warning and wait for user confirmation
    }

    // Check for pending deletions before final submission
    this.checkBulkDeletionAndProceed();
  }

  private checkBulkDeletionAndProceed() {
    // Check if there are any pending deletions (old system)
    const pendingDeletionsCount = this.pendingDeletions.size;

    // Check if there are any exercises marked for deletion (new system)
    const markedForDeletionCount = this.getMarkedForDeletionCount();

    const totalDeletionsCount = pendingDeletionsCount + markedForDeletionCount;

    console.log('Save confirmation check:', {
      pendingDeletionsCount,
      markedForDeletionCount,
      totalDeletionsCount,
      pendingDeletionExercises: this.getPendingDeletionExercises(),
    });

    if (totalDeletionsCount > 0) {
      this.bulkDeleteCount = totalDeletionsCount;
      this.showBulkDeleteConfirmation = true;
      console.log(
        'Showing bulk delete confirmation for',
        totalDeletionsCount,
        'exercises'
      );
      return; // Show bulk deletion confirmation
    }

    // No pending deletions, proceed with submission directly
    console.log('No deletions detected, proceeding directly with save');
    this.executeSubmission();
  }

  private executeSubmission() {
    // The persistence service now handles both old pendingDeletions and new marked exercises
    // No need to handle deletions here - just call the persistence service directly
    this.saveUpdatedAllotments();
  }

  private saveUpdatedAllotments() {
    // Use the persistence service to save the fitness plan
    console.log(
      'Saving fitness plan with pending deletions:',
      this.pendingDeletions
    );
    console.log('Component - fitnessPlanName being sent:', this.fitnessPlanName);
    console.log('Component - fitnessPlanName type:', typeof this.fitnessPlanName);
    console.log('Component - fitnessPlanName length:', this.fitnessPlanName?.length);

    this.isSubmitting = true;
    
    this.fitnessPlanPersistenceService
      .saveFitnessPlan(
        this.dateList,
        this.memberId,
        this.availableExercisesData,
        this.isEditMode,
        this.pendingDeletions,
        this.fitnessPlanName
      )
      .subscribe({
        next: (response) => {
          const actionText = this.isEditMode ? 'updated' : 'created';
          console.log(`Fitness plan ${actionText} successfully:`, response);

          // Clear pending deletions after successful save
          this.pendingDeletions.clear();

          // Hide loader and go to success step
          this.isSubmitting = false;
          this.currentStep = 3;
        },
        error: (error) => {
          const actionText = this.isEditMode ? 'updating' : 'creating';
          console.error(`Error ${actionText} fitness plan:`, error);

          // Hide loader and show validation modal with message
          this.isSubmitting = false;
          this.validationErrors = ['Unable to assign plan. Please try again.'];
          this.showValidationErrors = true;
        },
      });
  }

  // Method to validate all exercises across all dates
  validateAllExercises(): string[] {
    return this.fitnessPlanPersistenceService.validateAllExercises(
      this.dateList,
      this.exerciseDataService
    );
  }

  // Validation methods using services
  validateExerciseCompleteness(exercise: Exercise): boolean {
    return this.exerciseValidationService.isExerciseComplete(exercise);
  }

  validateAllExercisesInDate(dateIndex: number): boolean {
    if (dateIndex >= this.dateList.length) return false;
    const dateItem = this.dateList[dateIndex];
    return this.exerciseValidationService.areAllExercisesComplete(
      dateItem.exercises
    );
  }

  getIncompleteExercisesCount(dateIndex: number): number {
    if (dateIndex >= this.dateList.length) return 0;
    const dateItem = this.dateList[dateIndex];
    return this.exerciseStatisticsService.getIncompleteExercisesCount(
      dateItem.exercises,
      this.exerciseValidationService
    );
  }

  // Method to close validation error popup
  closeValidationErrors() {
    this.showValidationErrors = false;
    this.validationErrors = [];
  }

  // Method to handle success modal continue button
  // Method to handle success step continue button
  onSuccessStepContinue() {
    this.router.navigate(['/fitness-plan']);
  }

  goBack() {
    if (this.currentStep > 1) {
      this.previousStep();
    } else {
      this.router.navigate(['/fitness-plan']);
    }
  }

  getDurationDisplay(): string {
    if (!this.durationInDays) return '';

    const days = this.durationInDays;
    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;

    if (days === 7) {
      return '1 week';
    } else if (days < 7) {
      return `${days} day${days !== 1 ? 's' : ''}`;
    } else if (remainingDays === 0) {
      return `${weeks} week${weeks !== 1 ? 's' : ''}`;
    } else {
      return `${days} days (${weeks} week${
        weeks !== 1 ? 's' : ''
      } + ${remainingDays} day${remainingDays !== 1 ? 's' : ''})`;
    }
  }

  getFormattedHeight(): string {
    if (this.memberDetails.height) {
      return Math.trunc(this.memberDetails.height).toString();
    }
    return '---';
  }

  getFormattedWeight(): string {
    if (this.memberDetails.weight_lb) {
      return Math.trunc(this.memberDetails.weight_lb).toString() + ' LB';
    }
    return '---';
  }

  // Helper methods for exercise sections
  getExercisesBySection(
    dateIndex: number,
    section: 'warmup' | 'main' | 'cooldown'
  ): Exercise[] {
    // Return all exercises for display (including marked for deletion)
    return this.dateList[dateIndex].exercises.filter(
      (exercise) => exercise.section === section
    );
  }

  // Method to get only active exercises (not marked for deletion) for counting
  getActiveExercisesBySection(
    dateIndex: number,
    section: 'warmup' | 'main' | 'cooldown'
  ): Exercise[] {
    return this.dateList[dateIndex].exercises.filter(
      (exercise) =>
        exercise.section === section && !exercise.isMarkedForDeletion
    );
  }

  getSectionDisplayName(section: 'warmup' | 'main' | 'cooldown'): string {
    const sectionNames = {
      warmup: 'Warm Up',
      main: 'Main Exercise',
      cooldown: 'Cool Down',
    };
    return sectionNames[section];
  }

  getSectionExerciseCount(
    dateIndex: number,
    section: 'warmup' | 'main' | 'cooldown'
  ): number {
    // Count only active exercises (not marked for deletion) with non-empty names
    return this.getActiveExercisesBySection(dateIndex, section).filter(
      (exercise) => exercise.exercise.trim() !== ''
    ).length;
  }

  // Method to populate existing data when in edit mode
  populateExistingData() {
    const result = this.dataPopulationService.populateExistingData(
      this.existingAllotments,
      this.startDate,
      this.endDate,
      () => this.onEndDateChange(),
      () => this.generateDateList(),
      () => this.populateExerciseData()
    );

    this.startDate = result.startDate;
    this.endDate = result.endDate;
  }

  // Method to populate exercise data from existing allotments
  populateExerciseData() {
    this.dataPopulationService.populateExerciseData(
      this.dateList,
      this.existingAllotments,
      this.availableExercisesData,
      this.exerciseDataService
    );
  }

  // Update duration when end date changes
  onEndDateChange(newEndDate?: Date) {
    // If called with an argument (from date picker), use that date
    if (newEndDate) {
      this.endDate = newEndDate;
    }

    if (this.startDate && this.endDate) {
      // Calculate the difference in time (milliseconds)
      const timeDiff = this.endDate.getTime() - this.startDate.getTime();

      // Convert to days and add 1 for inclusive range
      this.durationInDays = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;

      // Update weeks (keep as fractional for precise calculation)
      this.duration = this.durationInDays / 7;

      console.log('Date range updated:', this.startDate, 'to', this.endDate);
      console.log('Duration in days:', this.durationInDays);

      // Check for existing fitness plan with the new date range
      this.checkForExistingFitnessPlan(this.startDate, this.endDate);

      // Update the date list
      this.generateDateList();

      // If we're in edit mode and have existing allotments, repopulate the data
      if (this.isEditMode && this.existingAllotments.length > 0) {
        console.log('Re-populating exercise data after date range change');
        this.populateExerciseData();
      }

      // Fetch exercise allotments for restore functionality if template is selected
      if (this.isTemplateSelected && this.memberId) {
        this.fetchExerciseAllotments();
      } else if (!this.isTemplateSelected && this.memberId) {
        // Auto-fill exercises from allotments when no template is selected
        this.autoFillExercisesFromAllotments();
      }
    }
  }

  // Update start date and recalculate duration
  onStartDateChange(newStartDate: Date) {
    this.startDate = newStartDate;

    // If we have an end date, recalculate duration and check for existing fitness plan
    if (this.endDate) {
      this.onEndDateChange();
    } else {
      // If no end date yet, just check for existing fitness plan with start date only
      this.checkForExistingFitnessPlan(this.startDate, this.endDate || undefined);
    }

    // Apply template if one is selected and we have both dates
    if (this.selectedTemplate && this.endDate) {
      console.log(
        'Start date changed, regenerating date list and applying template'
      );
      this.generateDateList();
      
      // Fetch exercise allotments for restore functionality
      if (this.isTemplateSelected && this.memberId) {
        this.fetchExerciseAllotments();
      } else if (!this.isTemplateSelected && this.memberId) {
        // Auto-fill exercises from allotments when no template is selected
        this.autoFillExercisesFromAllotments();
      }
    }
  }

  // Get minimum date for end date picker (should be start date or no restriction)
  getMinEndDate(): Date | undefined {
    // If start date is selected, end date should be at least the start date
    // If no start date is selected and no minDate restriction, return undefined (no restriction)
    return this.startDate || this.minDate || undefined;
  }

  // Time format conversion methods - Updated for MM:SS format with separate fields
  updateTimeFromSeparateFields(set: ExerciseSet) {
    this.formInputService.updateTimeFromSeparateFields(set);
  }

  validateMinutesInput(event: any, set: ExerciseSet) {
    this.formInputService.validateMinutesInput(event, set);
  }

  validateSecondsInput(event: any, set: ExerciseSet) {
    this.formInputService.validateSecondsInput(event, set);
  }

  // Parse existing MM:SS time string into separate minutes and seconds fields
  parseSeparateTimeFields(set: ExerciseSet) {
    this.formInputService.parseSeparateTimeFields(set);
  }

  // Legacy methods kept for backward compatibility but updated for MM:SS format
  enforceTimeFormat(event: any, set: ExerciseSet) {
    this.formInputService.enforceTimeFormat(event, set);
  }

  validateTimeFormat(event: any, set: ExerciseSet) {
    this.formInputService.validateTimeFormat(event, set);
  }

  // Set completion tracking methods
  isSetCompleted(set: ExerciseSet): boolean {
    return this.exerciseValidationService.isSetCompleted(set);
  }

  getTotalSetsCount(dateIndex: number): number {
    if (dateIndex >= this.dateList.length) return 0;
    return this.exerciseStatisticsService.getTotalSetsCount(
      this.dateList[dateIndex]
    );
  }

  getCompletedSetsCount(dateIndex: number): number {
    if (dateIndex >= this.dateList.length) return 0;
    return this.exerciseStatisticsService.getCompletedSetsCount(
      this.dateList[dateIndex]
    );
  }

  hasCompletedExercises(dateIndex: number): boolean {
    return this.getCompletedSetsCount(dateIndex) > 0;
  }

  // Helper method to check if a date is non-editable (current or past date)
  isDateNonEditable(date: Date): boolean {
    return this.dateUtilityService.isDateCurrentOrInPast(date);
  }

  // Helper method to check if a date is today (current date)
  isToday(date: Date): boolean {
    return this.dateUtilityService.isToday(date);
  }

  // Helper method to check if a date is strictly in the past (before today)
  isStrictlyInPast(date: Date): boolean {
    return this.dateUtilityService.isStrictlyInPast(date);
  }

  // Template-related methods
  loadTemplates(): void {
    this.isLoadingTemplates = true;
    this.fitnessPlanService.getAllFitnessPlanTemplates().subscribe(
      (response: any) => {
        if (
          response &&
          response.templates &&
          Array.isArray(response.templates)
        ) {
          this.availableTemplates = response.templates;
          console.log('Loaded templates:', this.availableTemplates);
        } else {
          this.availableTemplates = [];
          console.warn('No templates found or invalid response format');
        }
        this.isLoadingTemplates = false;
      },
      (error) => {
        console.error('Error loading templates:', error);
        this.availableTemplates = [];
        this.isLoadingTemplates = false;
      }
    );
  }

  onTemplateChange(templateId: string): void {
    this.selectedTemplateId = templateId;
    this.selectedTemplate =
      this.availableTemplates.find((t) => t.id === templateId) || null;
    console.log('Selected template:', this.selectedTemplate);
    console.log('Template ID:', templateId);

    if (this.selectedTemplate) {
      console.log('Template days structure:', this.selectedTemplate.days);
      console.log('Template name:', this.selectedTemplate.name);

      // Set template selection flags for restore functionality
      this.isTemplateSelected = true;

      // Autofill fitness plan name with template name
      this.fitnessPlanName = this.selectedTemplate.name;
      console.log('Fitness plan name autofilled with template name:', this.fitnessPlanName);

      // Fetch exercise allotments for restore functionality
      if (this.memberId && this.startDate && this.endDate) {
        this.fetchExerciseAllotments();
      }

      // Apply template immediately if we already have a date range
      if (this.dateList.length > 0) {
        console.log('Date list exists, applying template immediately');
        this.applyTemplateToDateRange();
      }
    } else {
      // Template deselected - reload original data
      console.log('Template deselected - reverting to original data');
      
      // Clear template selection flags
      this.isTemplateSelected = false;
      this.exerciseAllotments = [];
      
      this.revertToOriginalData();
      
      // Auto-fill exercises from allotments when template is deselected
      if (this.memberId && this.startDate && this.endDate) {
        this.autoFillExercisesFromAllotments();
      }
      
      // Clear fitness plan name when template is deselected
      this.fitnessPlanName = '';
      console.log('Fitness plan name cleared as template was deselected');
    }
  }

  /**
   * Get the next available exercise ID
   */
  private getNextExerciseId(): number {
    let maxId = 0;
    this.dateList.forEach(dateItem => {
      dateItem.exercises.forEach((exercise: Exercise) => {
        if (exercise.id > maxId) {
          maxId = exercise.id;
        }
      });
    });
    return maxId + 1;
  }

  /**
   * Add exercise fields from allotments that are not present in the template
   * This allows users to see and restore data from previous exercises
   */
  addAllotmentExercisesNotInTemplate(): void {
    if (!this.isTemplateSelected || !this.exerciseAllotments || this.exerciseAllotments.length === 0) {
      console.log('Skipping addAllotmentExercisesNotInTemplate: conditions not met', {
        isTemplateSelected: this.isTemplateSelected,
        hasAllotments: !!this.exerciseAllotments,
        allotmentsCount: this.exerciseAllotments?.length || 0
      });
      return;
    }

    console.log('Adding allotment exercises not in template...', {
      dateListLength: this.dateList.length,
      allotmentsCount: this.exerciseAllotments.length
    });

    let addedCount = 0;

    // Process each date
    this.dateList.forEach((dateItem, dateIndex) => {
      const dateString = this.formatDateForApi(dateItem.date);

      // Get all allotments for this date
      const dateAllotments = this.exerciseAllotments.filter(
        (allotment) =>
          allotment.memberId === this.memberId &&
          allotment.date === dateString
      );

      if (dateAllotments.length === 0) {
        return; // No allotments for this date
      }

      console.log(`Processing date ${dateString}, found ${dateAllotments.length} allotments`);

      // Process each phase (warmup, main, cooldown)
      ['warmup', 'main', 'cooldown'].forEach((phase) => {
        // Get allotments for this phase
        const phaseAllotments = dateAllotments.filter(
          (allotment) => allotment.phase === phase
        );

        if (phaseAllotments.length === 0) {
          return; // No allotments for this phase
        }

        // Get existing exercise IDs in the template for this phase
        // Convert to strings for consistent comparison
        const existingExerciseIds = dateItem.exercises
          .filter((ex: Exercise) => ex.section === phase)
          .map((ex: Exercise) => String(ex.exerciseId));

        console.log(`Phase ${phase} - existing exercise IDs:`, existingExerciseIds);
        console.log(`Phase ${phase} - allotment exercise IDs:`, phaseAllotments.map(a => String(a.exerciseId)));

        // Find allotment exercises that are NOT in the template
        // Convert both to strings for consistent comparison
        const missingAllotments = phaseAllotments.filter(
          (allotment) => !existingExerciseIds.includes(String(allotment.exerciseId))
        );

        // Add new exercise fields for missing allotments
        missingAllotments.forEach((allotment) => {
          // Resolve exercise name
          const exerciseName = this.exerciseDataService.resolveExerciseName(
            allotment.exerciseId,
            this.availableExercisesData
          );

          // Create empty exercise with the allotment's exercise ID
          const newExercise: Exercise = {
            id: this.getNextExerciseId(),
            exercise: exerciseName,
            exerciseId: allotment.exerciseId,
            section: phase as 'warmup' | 'main' | 'cooldown',
            sets: [
              {
                setNumber: 1,
                reps: 0,
                weights: 0,
                time: '00:00',
                timeMinutes: 0,
                timeSeconds: 0,
                status: 'new'
              }
            ],
            allotmentId: undefined // Will be set when restored
          };

          // Add the exercise to the date item
          dateItem.exercises.push(newExercise);
          addedCount++;

          console.log(`Added allotment exercise "${exerciseName}" (ID: ${allotment.exerciseId}) to ${dateItem.formattedDate} ${phase} with empty data`);
        });
      });
    });

    console.log(`Finished adding allotment exercises not in template. Total added: ${addedCount}`);
  }

  applyTemplateToDateRange(): void {
    if (!this.selectedTemplate || !this.dateList.length) {
      console.log('No template selected or no date list available');
      return;
    }

    console.log('Applying template to date range:', this.selectedTemplate);

    // Use the template management service to apply the template
    this.dateList = this.templateManagementService.applyTemplateToDateRange(
      this.selectedTemplate,
      this.dateList,
      this.pendingDeletions,
      (exerciseId: any) =>
        this.exerciseDataService.resolveExerciseName(
          exerciseId,
          this.availableExercisesData
        )
    );

    console.log('Template applied to date list:', this.dateList);
    
    // After template is applied, add exercises from allotments that are not in the template
    // This must happen AFTER template application to avoid being overwritten
    if (this.exerciseAllotments && this.exerciseAllotments.length > 0) {
      this.addAllotmentExercisesNotInTemplate();
    }
    
    this.showTemplateFeedback();
  }

  private showTemplateFeedback(): void {
    // Count how many exercises were marked for deletion
    const deletionCount = this.pendingDeletions.size;

    if (deletionCount > 0) {
      console.log(
        `Template successfully applied! ${deletionCount} existing exercise(s) marked for deletion.`
      );
      // You could show a toast notification here informing users about marked exercises
    } else {
      console.log('Template successfully applied to date range!');
    }

    // For now, we'll just log it. You could add a toast notification service later
  }

  private checkForNoDatesToUpdate(): boolean {
    // Get today's date for filtering
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check if all dates in the date list are past dates
    const allDatesArePast = this.dateList.every((dateItem) => {
      const itemDate = new Date(dateItem.date);
      itemDate.setHours(0, 0, 0, 0);
      return itemDate < today;
    });

    return allDatesArePast;
  }

  // Method to dismiss no dates to update warning
  dismissNoDatesToUpdateWarning() {
    this.showNoDatesToUpdateWarning = false;
  }

  // Method to confirm bulk deletion and proceed with submission
  confirmBulkDeletion() {
    this.showBulkDeleteConfirmation = false;
    this.executeSubmission();
  }

  // Method to cancel bulk deletion and go back
  cancelBulkDeletion() {
    this.showBulkDeleteConfirmation = false;
    this.bulkDeleteCount = 0;
    // Don't proceed with submission, let user modify their deletions
  }

  // Method to get count of exercises marked for deletion
  private getMarkedForDeletionCount(): number {
    let count = 0;
    console.log('Checking for marked exercises...');
    this.dateList.forEach((dateItem) => {
      dateItem.exercises.forEach((exercise) => {
        console.log('Exercise:', {
          name: exercise.exercise,
          isMarkedForDeletion: exercise.isMarkedForDeletion,
          allotmentId: exercise.allotmentId,
          hasAllotmentId: !!exercise.allotmentId,
        });
        if (exercise.isMarkedForDeletion && exercise.allotmentId) {
          count++;
          console.log(`Counted marked exercise: ${exercise.exercise}`);
        }
      });
    });
    console.log('Total marked for deletion count:', count);
    return count;
  }

  // Method to get details of exercises marked for deletion
  private getMarkedForDeletionDetails(): string[] {
    const details: string[] = [];
    this.dateList.forEach((dateItem) => {
      dateItem.exercises.forEach((exercise) => {
        if (exercise.isMarkedForDeletion && exercise.allotmentId) {
          details.push(`${dateItem.formattedDate}: ${exercise.exercise}`);
        }
      });
    });
    return details;
  }

  // Method to get list of exercises marked for deletion (for display in modal)
  getPendingDeletionExercises(): { date: string; exercise: string }[] {
    const deletionList: { date: string; exercise: string }[] = [];

    this.dateList.forEach((dateItem) => {
      // Check for rest day allotments staged for deletion
      if (
        dateItem.restDayAllotmentId &&
        this.pendingDeletions.has(dateItem.restDayAllotmentId)
      ) {
        deletionList.push({
          date: dateItem.formattedDate,
          exercise: 'Rest Day Entry',
        });
      }

      // Check for exercise allotments staged for deletion
      dateItem.exercises.forEach((exercise) => {
        // Include exercises marked for deletion (new system)
        if (exercise.isMarkedForDeletion && exercise.allotmentId) {
          deletionList.push({
            date: dateItem.formattedDate,
            exercise: exercise.exercise,
          });
        }
        // Include exercises in pendingDeletions set (old system)
        else if (
          exercise.allotmentId &&
          this.pendingDeletions.has(exercise.allotmentId)
        ) {
          deletionList.push({
            date: dateItem.formattedDate,
            exercise: exercise.exercise,
          });
        }
      });

      // Also check existing allotments that might be staged for deletion
      // but not currently represented in the dateList
      this.existingAllotments.forEach((allotment) => {
        if (this.pendingDeletions.has(allotment.id)) {
          const allotmentDate =
            this.dateUtilityService.formatISODateForComparison(allotment.date);
          const itemDate = this.dateUtilityService.formatDateForAPI(
            dateItem.date
          );

          if (allotmentDate === itemDate) {
            // Check if this allotment is a rest day
            if (allotment.phase === 'restday' || allotment.exerciseId === '') {
              // Only add if not already added above
              const alreadyAdded = deletionList.some(
                (item) =>
                  item.date === dateItem.formattedDate &&
                  item.exercise === 'Rest Day Entry'
              );
              if (!alreadyAdded) {
                deletionList.push({
                  date: dateItem.formattedDate,
                  exercise: 'Rest Day Entry',
                });
              }
            }
          }
        }
      });
    });

    return deletionList;
  }

  // Method to check if an exercise is marked for deletion
  isExerciseMarkedForDeletion(exercise: any): boolean {
    return exercise.isMarkedForDeletion || false;
  }

  /**
   * Undo the conversion of a workout day to rest day by restoring marked exercises
   * @param index - The index of the date item to undo
   */
  undoRestDayConversion(index: number): void {
    const dateItem = this.dateList[index];
    this.exerciseDataService.undoRestDayConversion(dateItem);
  }

  /**
   * Check if a date can be undone (has exercises marked for deletion)
   * @param index - The index of the date item to check
   * @returns true if the date can be undone
   */
  canUndoRestDayConversion(index: number): boolean {
    const dateItem = this.dateList[index];
    return this.exerciseDataService.canUndoRestDayConversion(dateItem);
  }

  /**
   * Reverts to original data when template is deselected
   */
  private revertToOriginalData(): void {
    // Clear pending deletions when reverting
    this.pendingDeletions.clear();

    if (
      this.isEditMode &&
      this.existingAllotments &&
      this.existingAllotments.length > 0
    ) {
      // We have original data from API - reload it
      console.log('Reverting to original data from API');
      this.populateExistingData();
    } else {
      // No original data - generate empty date list
      console.log('No original data - generating empty date list');
      this.generateDateList();
    }
  }
}
